import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import SectionBox from '@/components/SectionBox';
import UploadLoading, { UploadLoadingType } from '@/components/UploadLoading';
import { CandidateAttachment } from '@/types/api';
import * as Styles from './CandidateAttachments.styles';
import { getFileInputAcceptType } from './CandidateAttachments.utils';
import DeleteButton from '../DeleteButton';

type AttachmentByCategory = {
  label: string;
  category: string;
  id: string;
  files: Pick<CandidateAttachment, 'name' | 'attachedBy' | 'updatedAt' | 'size' | 'url' | 'id'>[];
};

type CandidateAttachmentsProps = {
  onSelectFile: (e: React.ChangeEvent<HTMLInputElement>, category: string) => void;
  handleDeleteAttachment: (attachmentId: string) => void;
  attachments: AttachmentByCategory[];
  uploadLoadingInfo?: UploadLoadingType;
};

const CandidateAttachments = function ({
  onSelectFile,
  uploadLoadingInfo,
  handleDeleteAttachment,
  attachments,
}: CandidateAttachmentsProps): JSX.Element {
  return (
    <SectionBox data-testid="attachments-section" title="Attachments" id="attachments">
      <Styles.Container>
        <Styles.Table>
          <thead>
            <Styles.TRow>
              <Styles.LargeTH>
                <Styles.GrayText />
              </Styles.LargeTH>
              <th>
                <Styles.GrayText />
              </th>

              <th>
                <Styles.GrayText>File Name</Styles.GrayText>
              </th>
              <Styles.MediumTH>
                <Styles.GrayText>Attached By</Styles.GrayText>
              </Styles.MediumTH>
              <Styles.SmallTH>
                <Styles.GrayText>Modified</Styles.GrayText>
              </Styles.SmallTH>
              <Styles.SmallTH>
                <Styles.GrayText>Size</Styles.GrayText>
              </Styles.SmallTH>
              <th>
                <Styles.GrayText />
              </th>
              <Styles.SmallTH>
                <Styles.GrayText />
              </Styles.SmallTH>
            </Styles.TRow>
          </thead>
          <tbody data-testid="attachment-data">
            {attachments.map((category) => {
              return (
                <Styles.TRow key={category.category}>
                  <Styles.TdLabels>
                    <Styles.GrayTextLine>{category.label}</Styles.GrayTextLine>
                  </Styles.TdLabels>
                  <Styles.TDataButton>
                    <Styles.Label htmlFor={`input-${category.category}`}>
                      <input
                        data-testid="upload-attachment"
                        id={`input-${category.category}`}
                        accept={getFileInputAcceptType(category.category)}
                        type="file"
                        multiple
                        onChange={(event) => onSelectFile(event, category.id)}
                      />
                      <AddIcon style={{ width: '12px' }} />
                    </Styles.Label>
                  </Styles.TDataButton>

                  <Styles.TData>
                    <>
                      {category.files.map((file) => (
                        <Styles.TFileName
                          key={`file-name-${file.id}`}
                          data-testid="filename-anchor"
                          title={file.name}
                          href={file.url}
                        >
                          {file.name}
                        </Styles.TFileName>
                      ))}
                      <UploadLoading {...uploadLoadingInfo} showIcon category={category.id} />
                    </>
                  </Styles.TData>

                  <Styles.TData>
                    {category.files.map((file) => (
                      <Styles.CellContent key={`file-attached-by-${file.id}`}>
                        {file.attachedBy}
                      </Styles.CellContent>
                    ))}
                    <UploadLoading {...uploadLoadingInfo} category={category.id} />
                  </Styles.TData>
                  <Styles.TData>
                    {category.files.map((file) => (
                      <Styles.CellContent key={`file-date-${file.id}`}>
                        {file.updatedAt}
                      </Styles.CellContent>
                    ))}
                    <UploadLoading {...uploadLoadingInfo} category={category.id} />
                  </Styles.TData>
                  <Styles.TData>
                    {category.files.map((file) => (
                      <Styles.CellContent key={`file-size-${file.id}`}>
                        {file.size}
                      </Styles.CellContent>
                    ))}
                    <UploadLoading {...uploadLoadingInfo} category={category.id} />
                  </Styles.TData>

                  <Styles.TData>
                    {category.files.map((file) => (
                      <Styles.ButtonContent key={`file-actions-${file.id}`}>
                        <Styles.DownloadBtn
                          href={file.url}
                          download={file.url}
                          data-testid="download-attachment"
                        >
                          <DownloadIcon style={{ width: '12px' }} />
                          Download
                        </Styles.DownloadBtn>
                      </Styles.ButtonContent>
                    ))}
                    <UploadLoading {...uploadLoadingInfo} category={category.id} />
                  </Styles.TData>
                  <Styles.TData>
                    {category.files.map((file) => (
                      <Styles.ButtonContent key={`file-actions-${file.id}`}>
                        <DeleteButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteAttachment(file.id);
                          }}
                          data-testid="delete-attachment"
                        />
                      </Styles.ButtonContent>
                    ))}
                    <UploadLoading {...uploadLoadingInfo} category={category.id} />
                  </Styles.TData>
                </Styles.TRow>
              );
            })}
          </tbody>
        </Styles.Table>
      </Styles.Container>
    </SectionBox>
  );
};

export default CandidateAttachments;
